<template>
	<div
		class="project-modify-modal-phases-tab"
		data-testid="project-modify-modal-phases-tab"
	>
		<ProjectModifyPhasesTabSkeleton v-show="isPhasesTaskSetsLoading" />
		<div v-show="!isPhasesTaskSetsLoading">
			<ProjectPhases
				class="project-modify-modal-phases-tab__phases-container"
				data-testid="project-modify-modal-phases-container"
				:project-id="Number(projectId)"
				:template-id="Number(templateId)"
				:quote-id="Number(quoteId)"
				:is-header-visible="false"
				:is-template="false"
				:local-phases-and-milestones-list="projectPhases"
				:local-has-back-to-back-phases="hasBackToBackPhases"
				:project-start-date="projectStartDate"
				:project-number-of-tasks="numberOfTasks"
				:append-to-body="true"
				@project-phases-updated="updateProjectPhases"
				@back-to-back-phases-updated="updateBackToBackPhases"
				@phases-task-sets-loading="phasesTasksSetsLoadingValue"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, ref } from 'vue';
import { type PhaseAndMilestoneListItemType } from '@/features/ProjectPhase';
import ProjectModifyPhasesTabSkeleton from '@/features/Project/components/ProjectModifyModal/ProjectModifyPhasesTabSkeleton.vue';
import {
	type Project,
	type ProjectTemplate,
	type Quote,
} from '@/generated/graphql';

const ProjectPhases = defineAsyncComponent(
	() => import('@/features/ProjectPhase/components/ProjectPhases')
);

export interface ProjectModifyModalPhasesTabProps {
	projectId: Project['id'];
	templateId?: ProjectTemplate['id'];
	quoteId?: Quote['id'];
	projectPhases: PhaseAndMilestoneListItemType[];
	hasBackToBackPhases: boolean;
	projectStartDate: NonNullable<Project['date']>;
	numberOfTasks: NonNullable<Project['numberOfTasks']>;
}

defineProps<ProjectModifyModalPhasesTabProps>();

const emit = defineEmits<{
	'phases-updated': [
		list: PhaseAndMilestoneListItemType[],
		snapshot: PhaseAndMilestoneListItemType[],
	];
	'back-to-back-updated': [hasBackToBackPhases: boolean, snapshot: boolean];
}>();

const isPhasesTaskSetsLoading = ref(true);

const updateProjectPhases = (payload: {
	list: PhaseAndMilestoneListItemType[];
	snapshot: PhaseAndMilestoneListItemType[];
}) => {
	emit('phases-updated', payload.list, payload.snapshot);
};

const updateBackToBackPhases = (payload: {
	hasBackToBackPhases: boolean;
	snapshot: boolean;
}) => {
	emit('back-to-back-updated', payload.hasBackToBackPhases, payload.snapshot);
};

const phasesTasksSetsLoadingValue = (payload: { isLoading: boolean }) => {
	isPhasesTaskSetsLoading.value = payload.isLoading;
};
</script>

<style lang="scss">
.project-modify-modal-phases-tab {
	&__optional-step-message {
		margin-bottom: 3rem;
	}

	.phases-and-milestones__actions,
	.phases-and-milestones__list {
		.project-phases-add-button {
			font-size: 1.5rem;
			font-weight: $scoro-fw-medium;
			line-height: 2rem;

			&.project-phases-add-button--strong {
				color: $blue-50;
			}
		}
	}

	.phases-and-milestones__actions {
		--sui-icon-fill: #{$blue-50};
	}
}
</style>
